import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  IconAdjustments,
  IconSailboat,
  IconReport,
  IconChevronDown,
  IconLogout,
  IconCategory,
  IconCode,
  IconPackage,
  IconRefresh,
  IconChartDots3,
  IconScale,
  IconClipboardData,
  IconClipboardList,
  IconCalendarEvent,
  IconFeather,
  IconTool,
  IconRobot,
  IconHammer,
  IconExternalLink,
} from "@tabler/icons-react";
import { useEvents } from "@/api/hooks";
import { useAuth } from "@/providers/auth-provider";
import { getAuthConfig } from "@/api/client";
import { AccountMenu } from "@/components/account-menu";
import { AddToHomescreen } from "@/components/add-to-homescreen";
import { UserAvatar } from "@/components/user-avatar";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { currentSeason, eventTiming, groupBySeason } from "@/lib/season";
import { SERVICING_URL } from "@/lib/servicing";

const STORAGE_KEY = "sailgp:lastEventId";
// Sentinel value for the "show all / current only" row inside the event
// switcher: it's a SelectItem so it's keyboard-navigable, but onValueChange
// intercepts it to toggle season visibility instead of selecting an event.
const TOGGLE_SEASONS_VALUE = "__toggle_seasons__";

function formatEventDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatEventRange(start: string, end?: string | null): string {
  if (!end || end === start) return formatEventDate(start);
  const s = new Date(start + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${s} – ${formatEventDate(end)}`;
}

// Parts and Maintenance moved to servicing.sailgp.tech at the 2026-09-28
// cutover; the sidebar keeps an entry for each, linking there.
const SERVICING_LINKS = [
  { label: "Parts", path: "/parts", Icon: IconTool },
  { label: "Maintenance", path: "/maintenance", Icon: IconHammer },
] as const;

export function Sidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: events } = useEvents();
  const { user, can } = useAuth();

  const match = location.pathname.match(/^\/events\/([^/]+)/);
  if (match) localStorage.setItem(STORAGE_KEY, match[1]);

  const selectedEventId = match?.[1] ?? localStorage.getItem(STORAGE_KEY);
  const selectedEvent = events?.find((e) => e.id === selectedEventId);

  // Event switcher: default to just the current season's events so the list
  // stays short once the calendar spans many seasons; a toggle reveals the rest.
  const [showAllSeasons, setShowAllSeasons] = useState(false);
  const activeSeason = currentSeason(events ?? []);
  const seasonGroups = groupBySeason(events ?? []);
  const visibleGroups =
    showAllSeasons || activeSeason === undefined
      ? seasonGroups
      : seasonGroups.filter((g) => g.season === activeSeason);
  const canToggleSeasons = seasonGroups.length > 1;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <nav
        className={`
          fixed top-0 left-0 bottom-0 w-[256px] bg-sidebar
          flex flex-col z-50 transition-transform duration-200
          lg:translate-x-0
          ${open ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Racing stripe */}
        <div className="absolute top-0 right-0 bottom-0 w-[3px] bg-sidebar-primary z-10" />

        {/* Logo */}
        <div className="shrink-0 px-5 pt-6 pb-5 bg-sidebar-accent">
          <img src="/sailgp-logo.png" alt="SailGP" className="w-full" />
        </div>

        {/* Navigation — scrolls independently; min-h-0 lets this flex child
            shrink below its content so overflow-y-auto engages, keeping the
            event switcher and profile below pinned and always visible. */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-2 pt-5">
          {/* Operations section — visible to any role that can view events;
              Builds drills into build detail so it stays gated on view-builds,
              while the read-only Schedule board is open to viewers/team users. */}
          {can("view-events") && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-[0.08em]">
                Operations
              </div>
              {selectedEventId && (
                <>
                  {can("view-builds") && (
                    <button
                      onClick={() => {
                        onClose();
                        navigate(`/events/${selectedEventId}`);
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-[7px] text-[14px] font-medium
                        rounded-md transition-colors mb-px
                        ${
                          location.pathname.match(/^\/events\/[^/]+$/)
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                        }`}
                    >
                      <IconSailboat className="h-4 w-4 opacity-50" />
                      Builds
                    </button>
                  )}
                  <button
                    onClick={() => {
                      onClose();
                      navigate(`/events/${selectedEventId}/schedule`);
                    }}
                    className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                      rounded-md transition-colors
                      ${
                        location.pathname.includes(
                          `/events/${selectedEventId}/schedule`,
                        )
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                      }`}
                  >
                    <IconCalendarEvent className="h-4 w-4 opacity-50" />
                    Schedule
                  </button>
                  {can("manage-releases") && (
                    <button
                      onClick={() => {
                        onClose();
                        navigate(`/events/${selectedEventId}/releases`);
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                        rounded-md transition-colors
                        ${
                          location.pathname.includes(
                            `/events/${selectedEventId}/releases`,
                          )
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                        }`}
                    >
                      <IconCode className="h-4 w-4 opacity-50" />
                      Releases
                    </button>
                  )}
                </>
              )}
            </>
          )}

          {can("view-team-report") && user.team && selectedEventId && (
            <button
              onClick={() => {
                onClose();
                navigate(`/events/${selectedEventId}/team`);
              }}
              className={`flex items-center gap-2 w-full px-3 py-[7px] text-[14px] font-medium
                rounded-md transition-colors mb-px
                ${
                  location.pathname.match(/^\/events\/[^/]+\/team$/)
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                }`}
            >
              <IconReport className="h-4 w-4 opacity-50" />
              Team Report
            </button>
          )}

          {/* Calibrations section */}
          {can("manage-calibrations") && (
            <div className="mt-5">
              <div className="px-3 py-1 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-[0.08em]">
                Calibrations
              </div>
              <button
                onClick={() => {
                  onClose();
                  navigate("/admin/calibration-sync");
                }}
                className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                  rounded-md transition-colors
                  ${
                    location.pathname.includes("/admin/calibration-sync")
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  }`}
              >
                <IconAdjustments className="h-4 w-4 opacity-50" />
                Board Cals
              </button>
              {selectedEventId && can("manage-releases") && (
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/events/${selectedEventId}/common-cals`);
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                    rounded-md transition-colors
                    ${
                      location.pathname.includes(
                        `/events/${selectedEventId}/common-cals`,
                      )
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                >
                  <IconRefresh className="h-4 w-4 opacity-50" />
                  Common Cals
                </button>
              )}
              {selectedEventId && (
                <button
                  onClick={() => {
                    onClose();
                    navigate(`/events/${selectedEventId}/wing-cals`);
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                    rounded-md transition-colors
                    ${
                      location.pathname.includes(
                        `/events/${selectedEventId}/wing-cals`,
                      )
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                >
                  <IconFeather className="h-4 w-4 opacity-50" />
                  Wing Cals
                </button>
              )}
            </div>
          )}

          {/* Measurements section — fleet-wide data, admin/editor only */}
          {can("view-fleet") && selectedEventId && (
            <div className="mt-5">
              <div className="px-3 py-1 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-[0.08em]">
                Measurements
              </div>
              {(
                [
                  { slug: "yaw", label: "Yaw" },
                  { slug: "steering", label: "Steering" },
                  { slug: "db-height", label: "DB Height" },
                ] as const
              ).map(({ slug, label }) => {
                const target = `/events/${selectedEventId}/fleet-measurements/${slug}`;
                const active = location.pathname.startsWith(target);
                return (
                  <button
                    key={slug}
                    onClick={() => {
                      onClose();
                      navigate(target);
                    }}
                    className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                      rounded-md transition-colors
                      ${
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                      }`}
                  >
                    <IconChartDots3 className="h-4 w-4 opacity-50" />
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Fleet section — event-independent. The Weight Report is the
              sanctioned viewer-visible fleet surface (view-fleet-report).
              Parts and Maintenance moved to servicing.sailgp.tech at the
              2026-09-28 cutover; staff (view-builds, the admin/editor roles
              that could open them here) get links to it. */}
          {(can("view-fleet-report") || can("view-builds")) && (
            <div className="mt-5">
              <div className="px-3 py-1 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-[0.08em]">
                Fleet
              </div>
              {can("view-fleet-report") && (
                <button
                  onClick={() => {
                    onClose();
                    navigate("/fleet-report");
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                    rounded-md transition-colors
                    ${
                      location.pathname.startsWith("/fleet-report")
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                >
                  <IconClipboardData className="h-4 w-4 opacity-50" />
                  Weight Report
                </button>
              )}
              {can("view-builds") && (
                <button
                  onClick={() => {
                    onClose();
                    navigate("/metrics");
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                    rounded-md transition-colors
                    ${
                      location.pathname.startsWith("/metrics")
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                >
                  <IconRobot className="h-4 w-4 opacity-50" />
                  Bot Metrics
                </button>
              )}
              {can("view-builds") &&
                SERVICING_LINKS.map(({ label, path, Icon }) => (
                  <a
                    key={path}
                    href={`${SERVICING_URL}${path}`}
                    onClick={onClose}
                    title={`${label} is in servicing.sailgp.tech`}
                    className="flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                      rounded-md transition-colors
                      text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  >
                    <Icon className="h-4 w-4 opacity-50" />
                    {label}
                    <IconExternalLink className="ml-auto h-3.5 w-3.5 opacity-40" aria-hidden />
                    <span className="sr-only">(opens servicing.sailgp.tech)</span>
                  </a>
                ))}
            </div>
          )}

          {/* Weighings section
              Fleet Equalization needs an event context; Manage Weighings is
              event-independent (weighings are boat-scoped), so we render the
              label whenever the user has view-builds and conditionally include
              the Fleet Equalization entry only when an event is selected. */}
          {can("view-builds") && (
            <div className="mt-5">
              <div className="px-3 py-1 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-[0.08em]">
                Weighings
              </div>
              {selectedEventId &&
                (() => {
                  const target = `/events/${selectedEventId}/fleet-equalization`;
                  const active = location.pathname.startsWith(target);
                  return (
                    <button
                      onClick={() => {
                        onClose();
                        navigate(target);
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                      rounded-md transition-colors
                      ${
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                      }`}
                    >
                      <IconScale className="h-4 w-4 opacity-50" />
                      Fleet Equalization
                    </button>
                  );
                })()}
              {(() => {
                const target = "/admin/weighings";
                const active = location.pathname.startsWith(target);
                return (
                  <button
                    onClick={() => {
                      onClose();
                      navigate(target);
                    }}
                    className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                      rounded-md transition-colors
                      ${
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                      }`}
                  >
                    <IconClipboardList className="h-4 w-4 opacity-50" />
                    Manage Weighings
                  </button>
                );
              })()}
            </div>
          )}

          {/* Admin section */}
          {can("manage-calibrations") && (
            <div className="mt-5">
              <div className="px-3 py-1 text-[10px] font-semibold text-sidebar-foreground/40 uppercase tracking-[0.08em]">
                Admin
              </div>
              {can("manage-events") && (
                <button
                  onClick={() => {
                    onClose();
                    navigate("/admin/events");
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                    rounded-md transition-colors
                    ${
                      location.pathname.includes("/admin/events")
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                >
                  <IconCalendarEvent className="h-4 w-4 opacity-50" />
                  Events
                </button>
              )}
              <button
                onClick={() => {
                  onClose();
                  navigate("/admin/photo-categories");
                }}
                className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                  rounded-md transition-colors
                  ${
                    location.pathname.includes("/admin/photo-categories")
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                  }`}
              >
                <IconCategory className="h-4 w-4 opacity-50" />
                Photo Categories
              </button>
              {can("manage-releases") && (
                <button
                  onClick={() => {
                    onClose();
                    navigate("/admin/releases");
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-[7px] text-[13px] font-medium
                    rounded-md transition-colors
                    ${
                      location.pathname.includes("/admin/releases")
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                    }`}
                >
                  <IconPackage className="h-4 w-4 opacity-50" />
                  Manage Releases
                </button>
              )}
            </div>
          )}
        </div>

        {/* Homescreen install — renders nothing once installed / unsupported */}
        <AddToHomescreen variant="sidebar" />

        {/* Event switcher */}
        {events && events.length > 0 && (
          <div className="shrink-0 border-t border-sidebar-border">
            <Select
              value={selectedEventId ?? ""}
              onValueChange={(id) => {
                if (id === TOGGLE_SEASONS_VALUE) {
                  setShowAllSeasons((v) => !v);
                  return;
                }
                onClose();
                const dest =
                  !can("view-builds") && user.team
                    ? `/events/${id}/team`
                    : `/events/${id}`;
                navigate(dest);
              }}
            >
              <SelectTrigger
                className="w-full border-0 rounded-none px-5 py-8 h-auto shadow-none
                  bg-transparent hover:bg-sidebar-accent/50 transition-colors
                  text-left focus:ring-0 focus-visible:ring-0
                  [&>svg:last-child]:hidden"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-sidebar-foreground/40 uppercase tracking-[0.08em]">
                      Event
                    </div>
                    <span className="sr-only">
                      <SelectValue placeholder="Select event" />
                    </span>
                    <div className="text-[14px] font-semibold text-sidebar-foreground truncate mt-1">
                      {selectedEvent?.name ?? "Select event"}
                    </div>
                    {selectedEvent?.start_date && (
                      <div className="text-[12px] text-sidebar-foreground/40 mt-1">
                        {formatEventRange(
                          selectedEvent.start_date,
                          selectedEvent.end_date,
                        )}
                      </div>
                    )}
                  </div>
                  <IconChevronDown className="h-4 w-4 text-sidebar-foreground/30 shrink-0 ml-2" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {visibleGroups.map((group) => (
                  <SelectGroup key={group.season}>
                    <SelectLabel>Season {group.season}</SelectLabel>
                    {group.events.map((ev) => {
                      // Timing at a glance: finished events recede to muted,
                      // upcoming ones keep full contrast, and the one
                      // happening now carries the single accent dot.
                      const timing = eventTiming(ev);
                      return (
                        <SelectItem
                          key={ev.id}
                          value={ev.id}
                          className={
                            timing === "past" ? "text-muted-foreground" : undefined
                          }
                        >
                          <span className="flex items-center gap-2">
                            {timing === "ongoing" && (
                              <span
                                aria-hidden
                                className="h-1.5 w-1.5 rounded-full bg-primary shrink-0"
                              />
                            )}
                            {ev.name}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                ))}
                {canToggleSeasons && (
                  <>
                    <SelectSeparator />
                    <SelectItem value={TOGGLE_SEASONS_VALUE}>
                      {showAllSeasons
                        ? "Show current season only"
                        : "Show all seasons…"}
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* User */}
        <div className="shrink-0 border-t border-sidebar-border px-5 py-5 space-y-3.5">
          <AccountMenu side="top" align="start">
            <button
              className="flex items-center gap-3 w-full text-left -mx-2 px-2 py-1.5 rounded-lg
                hover:bg-sidebar-accent/50 transition-colors"
              aria-label="Account settings"
            >
              <UserAvatar className="w-10 h-10" textClass="text-[13px]" />
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-sidebar-foreground truncate">
                  {user.name}
                </div>
                <div className="text-[12px] text-sidebar-foreground/40 truncate">
                  {user.email}
                </div>
              </div>
            </button>
          </AccountMenu>
          <button
            onClick={async () => {
              const { logoutUrl } = await getAuthConfig();
              window.location.href = logoutUrl;
            }}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 text-[14px] font-medium
              text-sidebar-foreground/60 bg-sidebar-accent rounded-lg
              hover:bg-sidebar-accent/70 hover:text-sidebar-foreground transition-colors"
          >
            <IconLogout className="h-4 w-4" />
            Logout
          </button>
        </div>
      </nav>
    </>
  );
}
